/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Inject, OnInit } from "@angular/core";
import { ActionSendEmailComponent } from "../action-send-email/action-send-email.component";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import {
    ActionsHttpServiceService,
    RequiredParamsForActionsHttpRequests,
} from "src/app/services/httpServices/actionsHttpService/actions-http-service.service";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { HttpErrorHandlerService } from "src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service";
import { NotificationsService } from "src/app/services/infra/notifications/notifications.service";
import { LabelsProviderPipe } from "src/app/pipes/labels-provider/labels-provider.pipe";
import { ZenityEntityActionResultCode } from "src/app/models/api/actions/zenityEntityActions";
import { Label } from "src/app/labels/englishLabels";
import { PopupPosition } from "../../copy-button/copy-button.component";
import { ActionGetRawListOutputs } from "src/app/models/api/actions/getRawJsonList.model";
import { BehaviorSubject, forkJoin, of } from "rxjs";

interface FlowRunModel {
    index: number;
    flowRun: { [key: string]: any };
    flowRunString: string;
    flowRunInputOutputFetched: boolean;
    flowRunTriggerInput: { [key: string]: any };
    flowRunTriggerOutput: { [key: string]: any };
}

@Component({
    selector: "app-get-flow-runs-action",
    templateUrl: "./get-flow-runs-action.component.html",
    styleUrls: ["./get-flow-runs-action.component.scss"],
})
export class GetFlowRunsActionComponent implements OnInit {
    public title = "";
    public description = "";
    public waitingForResponse = true;
    public noDataText = new BehaviorSubject<string>("");
    public popupPosition = PopupPosition;
    public selectedFlowRunIndex = 0;
    public flowRunsModels: FlowRunModel[] = [];

    constructor(
        private _labelsProviderPipe: LabelsProviderPipe,
        private _http: HttpClient,
        public dialogRef: MatDialogRef<ActionSendEmailComponent>,
        @Inject(MAT_DIALOG_DATA)
        public actionsParams: RequiredParamsForActionsHttpRequests,
        private _actionsHttpService: ActionsHttpServiceService,
        private _httpErrorHandlerService: HttpErrorHandlerService,
        private _notificationService: NotificationsService,
    ) {}

    ngOnInit(): void {
        this._actionsHttpService.getFlowRuns(this.actionsParams.integrationId, this.actionsParams.resourceId).subscribe({
            next: (response: ActionGetRawListOutputs) => {
                if (response.resultCode != ZenityEntityActionResultCode.success) {
                    this.waitingForResponse = false;
                    const message = `${response.title}. ${response.description ?? ""}`;
                    this._notificationService.error(message);
                    this.noDataText.next("Failed to get data");
                } else {
                    if (response.rawJsonItems.length == 0) {
                        this.noDataText.next("No flow runs found");
                        this.waitingForResponse = false;
                    }
                    for (let i = 0; i < response.rawJsonItems.length; i++) {
                        this.flowRunsModels.push({
                            flowRun: response.rawJsonItems[i],
                            flowRunInputOutputFetched: false,
                            index: i,
                            flowRunTriggerOutput: {},
                            flowRunTriggerInput: {},
                            flowRunString: JSON.stringify(response.rawJsonItems[i], null, "\t"),
                        });
                    }
                    this._getFlowRunInputOutputIfNeeded(0);

                    this.title = Label.get("commonActions.getFlowRunsAction.dialogTitle");
                    this.description = Label.get("commonActions.getFlowRunsAction.dialogDescription");
                }
            },
            error: (httpError: HttpErrorResponse) => {
                this.waitingForResponse = true;
                if (this._httpErrorHandlerService.isUnhandledExceptionError(httpError.status)) {
                    this._httpErrorHandlerService.handle(httpError);
                } else {
                    this._notificationService.error(httpError.error.detail);
                }
                this.noDataText.next("Failed to get data");
                this.waitingForResponse = false;
            },
        });
    }

    public closeWindow(): void {
        this.dialogRef.close();
    }

    public nextItem(event: MouseEvent): void {
        event.stopPropagation();
        if (this.selectedFlowRunIndex + 1 >= this.flowRunsModels.length) {
            return;
        }
        this.selectedFlowRunIndex += 1;
        this._getFlowRunInputOutputIfNeeded(this.selectedFlowRunIndex);
    }

    public prevItem(event: MouseEvent): void {
        event.stopPropagation();
        if (this.selectedFlowRunIndex - 1 < 0) {
            return;
        }
        this.selectedFlowRunIndex -= 1;
        this._getFlowRunInputOutputIfNeeded(this.selectedFlowRunIndex);
    }

    private _getFlowRunInputOutputIfNeeded(index: number): void {
        if (this.flowRunsModels.length == 0 || this.flowRunsModels[index].flowRunInputOutputFetched) {
            this.waitingForResponse = false;
            return;
        }
        this.waitingForResponse = true;
        const inputLink = this._getLinkUri(index, "inputsLink");
        const outputLink = this._getLinkUri(index, "outputsLink");

        let inputsRequest = of({});
        let outputsRequest = of({});

        if (inputLink !== "") {
            inputsRequest = this._http.get<any>(inputLink, {
                headers: {},
                params: {},
                responseType: "json",
            });
        }

        if (outputLink !== "") {
            outputsRequest = this._http.get<any>(outputLink, {
                headers: {},
                params: {},
                responseType: "json",
            });
        }

        forkJoin(inputsRequest, outputsRequest).subscribe({
            next: async (responses: any) => {
                this.flowRunsModels[index].flowRunTriggerInput = responses[0];
                this.flowRunsModels[index].flowRunTriggerOutput = responses[1];
                this.flowRunsModels[index].flowRunInputOutputFetched = true;
                this._constructFlowRunString(index);
                await new Promise((resolve) => setTimeout(resolve, 1000));
                this.waitingForResponse = false;
            },
            error: (httpError: HttpErrorResponse) => {
                this.waitingForResponse = false;
                this._notificationService.error(httpError.error.detail);
            },
        });
    }

    private _constructFlowRunString(index: number): void {
        const flowRun = this.flowRunsModels[index];

        const flowRunJson = {
            "Flow Run Details": flowRun.flowRun,
            "Trigger Input": flowRun.flowRunTriggerInput,
            "Trigger Output": flowRun.flowRunTriggerOutput,
        };
        const flowRunString = JSON.stringify(flowRunJson, null, "\t");

        flowRun.flowRunString = flowRunString;
    }

    private _getLinkUri(index: number, property: string): string {
        try {
            // if all path exists until uri, then result is undefined
            const uri = this.flowRunsModels[index].flowRun["properties"]["trigger"][property]["uri"];
            if (uri === undefined) {
                return "";
            }
            return uri;
        } catch {
            return "";
        }
    }
}
